import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
  NumberInput,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { ShipmentMonthlySummaryReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentMonthlySummary } from './exportShipmentReports';

export function ShipmentMonthlySummaryPage() {
  const labels = t().reports;
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState<ShipmentMonthlySummaryReport | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('fileNo');

  const rows = useMemo(
    () =>
      report
        ? sortItems(report.files, sort, {
            fileNo: (r) => r.fileNo,
            customerName: (r) => r.customer.name,
            status: (r) => r.status,
            gross: (r) => r.expenses.gross,
          })
        : [],
    [report, sort],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentMonthlySummary(month, year);
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentMonthlySummary({ report, rows, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentMonthlySummary({ report, rows, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.month}>
          <Select
            value={month}
            size="sm"
            showClearButton={false}
            options={t().common.months.map((name, i) => ({
              value: i + 1,
              label: name,
            }))}
            onChange={(e) => setMonth(Number(e.target.value))}
          />
        </FilterField>
        <FilterField label={labels.year}>
          <NumberInput
            value={year}
            allowDecimals={false}
            showClearButton={false}
            size="sm"
            onChange={(e) => {
              if (e.target.value != null) setYear(e.target.value);
            }}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report ? null : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="p-4">
              <p className="text-xs text-muted">{labels.fileCount}</p>
              <p className="text-xl font-semibold tabular-nums">
                {report.summary.fileCount}
              </p>
              <p className="mt-1 text-xs text-muted">
                {labels.shipmentStatusOpen}: {report.summary.byStatus.OPEN ?? 0}{' '}
                · {labels.shipmentStatusInvoiced}:{' '}
                {report.summary.byStatus.INVOICED ?? 0}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted">{labels.expenseGross}</p>
              <p className="text-xl font-semibold tabular-nums">
                {formatMoney(report.summary.expenses.gross)}
              </p>
              <p className="mt-1 text-xs text-muted">
                {labels.expenseNet} {formatMoney(report.summary.expenses.net)} ·{' '}
                {labels.expenseVat} {formatMoney(report.summary.expenses.vat)}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted">{labels.billable}</p>
              <p className="text-xl font-semibold tabular-nums">
                {formatMoney(report.summary.billable.gross)}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted">{labels.internal}</p>
              <p className="text-xl font-semibold tabular-nums">
                {formatMoney(report.summary.internal.gross)}
              </p>
            </Card>
          </div>

          <DataTablePanel className="min-h-0 flex-1">
            {rows.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead className={dataTableHeadClass}>
                  <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                    <SortableTh
                      sortKey="fileNo"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {labels.shipmentFileNo}
                    </SortableTh>
                    <SortableTh
                      sortKey="customerName"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().common.customer}
                    </SortableTh>
                    <SortableTh
                      sortKey="status"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {labels.shipmentStatus}
                    </SortableTh>
                    <th className="px-4 py-3 font-semibold">
                      {t().common.date}
                    </th>
                    <SortableTh
                      sortKey="gross"
                      sort={sort}
                      onSort={toggleSort}
                      className="text-right"
                    >
                      {labels.expenseGross}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-line/70">
                      <td className="px-4 py-3">
                        <Link
                          to={`/shipment-files/${row.id}`}
                          className="font-medium text-teal-700 hover:underline dark:text-teal-300"
                        >
                          {row.fileNo}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/customers/${row.customer.id}`}
                          className="hover:underline"
                        >
                          {row.customer.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {row.status === 'OPEN'
                          ? labels.shipmentStatusOpen
                          : labels.shipmentStatusInvoiced}
                      </td>
                      <td className="px-4 py-3">
                        {formatDate(row.effectiveDate)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(row.expenses.gross)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DataTablePanel>
        </>
      )}
    </PageShell>
  );
}
